import styles from './style.postcss';

import React, { Component } from 'react';
import { connect, testClass, PageCard, ProductionStatus, StudentCard } from 'omni-common-ui';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import {
  actions as RAv2HomeworkResultDetailActions,
  accessors as RAv2HomeworkResultDetailAccessors,
} from 'data/RAv2HomeworkResultDetail';
import { actions as Session } from 'data/SessionPlans';
import { getRAv2LevelNumber } from 'domain/CourseType';
import { parseProductionStatus } from 'domain/ProductionStatus';
import { getMonthAbbr } from 'domain/Month';
import { getWeekdayAbbr } from 'domain/Weekday';

const SUB_COLUMNS = ['Completion', 'Accuracy', 'Story Read', 'Recorded'];


function formatUnitLabel(unitInfo, groupLevel) {
  return `Level ${groupLevel || unitInfo.unitSequenceId}, Story ${unitInfo.unitSequenceId}`;
}

function formatUnitDate(dateStr) {
  if (! dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${getWeekdayAbbr(d.getDay())}, ${d.getDate()} ${getMonthAbbr(d.getMonth())} ${d.getFullYear()}`;
}

function formatPercentage(value) {
  return `${Math.round((value || 0) * 100)}%`;
}

class DigitalPracticeRAv2 extends Component {
  componentDidMount() {
    const { groupId } = this.props;
    if (groupId) {
      this.props.fetchRAv2HomeworkResultDetailIfNeeded(groupId);
    }
  }

  _getUnitInfos() {
    const { students } = this.props;
    if (! students || students.size === 0) return [];

    const unitMap = {};
    students.forEach((student) => {
      if (student.unitResults) {
        student.unitResults.forEach((unit) => {
          if (! unitMap[unit.unitSequenceId]) {
            unitMap[unit.unitSequenceId] = {
              unitId: unit.unitId,
              unitSequenceId: unit.unitSequenceId,
              level: unit.level,
              storyNumber: unit.storyNumber,
              date: unit.date,
            };
          }
        });
      }
    });

    return Object.values(unitMap).sort((a, b) => a.unitSequenceId - b.unitSequenceId);
  }

  _getUnitResultMap(student) {
    const map = {};
    if (student.unitResults) {
      student.unitResults.forEach((unit) => {
        map[unit.unitSequenceId] = unit;
      });
    }
    return map;
  }

  _renderInfoTooltip() {
    return (
      <div className={styles.DigitalPracticeRAv2_infoIcon}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="black" />
        </svg>
        <div className={styles.DigitalPracticeRAv2_tooltip}>
          <h3 className={styles.DigitalPracticeRAv2_tooltip_title}>Performance Legends</h3>
          <div className={styles.DigitalPracticeRAv2_tooltip_item}>
            <strong>Completion</strong>
            <p>Completion rate records include completing Step2 activities</p>
          </div>
          <div className={styles.DigitalPracticeRAv2_tooltip_item}>
            <strong>Accuracy</strong>
            <p>Accuracy rate is calculated based on the ratio of correct answers to total answers in the step 2 activities</p>
          </div>
          <div className={styles.DigitalPracticeRAv2_tooltip_item}>
            <strong>Story Read</strong>
            <p>Reading completion is marked as achieved when the student listen to and read the entire story once</p>
          </div>
          <div className={styles.DigitalPracticeRAv2_tooltip_item}>
            <strong>Recorded</strong>
            <p>Recordings are displayed based on how many times a student records the story, with each attempt contributing to the total count</p>
          </div>
        </div>
      </div>
    );
  }

  componentDidUpdate() {
    this._syncRowHeights();
  }

  _syncRowHeights() {
    if (! this._panelRef || ! this._tableRef) return;
    const panelTrs = this._panelRef.querySelectorAll('tr');
    const tableTrs = this._tableRef.querySelectorAll('tr');
    const len = Math.min(panelTrs.length, tableTrs.length);
    for (let i = 0; i < len; i++) {
      panelTrs[i].style.height = '';
      tableTrs[i].style.height = '';
    }
    for (let i = 0; i < len; i++) {
      const h = Math.max(panelTrs[i].offsetHeight, tableTrs[i].offsetHeight);
      panelTrs[i].style.height = `${h}px`;
      tableTrs[i].style.height = `${h}px`;
    }
  }

  _renderPanelHeader() {
    return (
      <thead>
        <tr>
          <th rowSpan="2" className={styles.DigitalPracticeRAv2_studentCol}>Student</th>
          <th rowSpan="2" className={styles.DigitalPracticeRAv2_statusCol}>Status</th>
        </tr>
        <tr />
      </thead>
    );
  }

  _isActiveUnit(unitId) {
    const { defaultUnitIds } = this.props;
    if (! defaultUnitIds || defaultUnitIds.length === 0) return false;
    return defaultUnitIds.some((id) => String(id) === String(unitId));
  }

  _renderMainTableHeader(unitInfos) {
    return (
      <thead>
        <tr>
          {unitInfos.map((unitInfo) => {
            const isActive = this._isActiveUnit(unitInfo.unitId);
            const headerClass = isActive
              ? `${styles.DigitalPracticeRAv2_unitHeader} ${styles.DigitalPracticeRAv2_unitHeaderActive}`
              : styles.DigitalPracticeRAv2_unitHeader;
            const dateFormatted = formatUnitDate(unitInfo.date);
            return (
              <th key={`unit-${unitInfo.unitSequenceId}`} colSpan="4" className={headerClass}>
                <div>{formatUnitLabel(unitInfo, this.props.groupLevel)}</div>
                {dateFormatted && (
                  <time className={styles.DigitalPracticeRAv2_unitHeaderDate} dateTime={unitInfo.date}>
                    {dateFormatted}
                  </time>
                )}
              </th>
            );
          })}
        </tr>
        <tr>
          {unitInfos.map((unitInfo) => {
            const isActive = this._isActiveUnit(unitInfo.unitId);
            return SUB_COLUMNS.map((col, colIndex) => {
              const subHeaderClass = [styles.DigitalPracticeRAv2_subHeader];
              if (colIndex === 0) subHeaderClass.push(styles.DigitalPracticeRAv2_unitFirstCol);
              if (isActive) subHeaderClass.push(styles.DigitalPracticeRAv2_subHeaderActive);
              return (
                <th
                    key={`sub-${unitInfo.unitSequenceId}-${col}`}
                    className={subHeaderClass.join(' ')}>
                  {col}
                </th>
              );
            });
          })}
        </tr>
      </thead>
    );
  }

  _renderPanelRow(student) {
    const info = student.studentGeneralInfo || {};
    const name = `${info.firstName || ''} ${info.lastName || ''}`.trim();
    const statusParsed = parseProductionStatus(student.status);

    return (
      <tr key={info.id}>
        <td className={styles.DigitalPracticeRAv2_studentCol}>
          <div className={styles.DigitalPracticeRAv2_studentCard}>
            <StudentCard borderless backgroundless>
              <StudentCard.Profile
                  name={name}
                  localName={info.localName}
                  gender={info.gender}
                  avatarUrl={info.avatarUrl} />
            </StudentCard>
          </div>
        </td>
        <td className={styles.DigitalPracticeRAv2_statusCol}>
          <ProductionStatus status={statusParsed.fullName} highlighted={student.isInProduction} />
        </td>
      </tr>
    );
  }

  _renderMainTableRow(student, unitInfos) {
    const info = student.studentGeneralInfo || {};
    const unitMap = this._getUnitResultMap(student);

    return (
      <tr key={info.id}>
        {unitInfos.map((unitInfo) => {
          const unit = unitMap[unitInfo.unitSequenceId] || {};
          const isActive = this._isActiveUnit(unitInfo.unitId);
          const firstColClass = isActive
            ? `${styles.DigitalPracticeRAv2_unitFirstCol} ${styles.DigitalPracticeRAv2_cellActive}`
            : styles.DigitalPracticeRAv2_unitFirstCol;
          const cellClass = isActive ? styles.DigitalPracticeRAv2_cellActive : undefined;
          return [
            <td key={`${info.id}-${unitInfo.unitSequenceId}-completion`} className={firstColClass}>
              {unit.completion ? formatPercentage(unit.completion) : 'N/A'}
            </td>,
            <td key={`${info.id}-${unitInfo.unitSequenceId}-accuracy`} className={cellClass}>
              {unit.completion ? formatPercentage(unit.accuracy) : 'N/A'}
            </td>,
            <td key={`${info.id}-${unitInfo.unitSequenceId}-storyRead`} className={cellClass}>
              {unit.storyRead >= 1
                ? <span className={styles.DigitalPracticeRAv2_checkmark}>&#10003;</span>
                : 'N/A'}
            </td>,
            <td key={`${info.id}-${unitInfo.unitSequenceId}-recorded`} className={cellClass}>
              {unit.recorded ? unit.recorded : 'N/A'}
            </td>,
          ];
        })}
      </tr>
    );
  }

  render() {
    const { students, isLoading } = this.props;
    const unitInfos = this._getUnitInfos();

    return (
      <PageCard className={testClass('digital-practice-rav2')}>
        <PageCard.Heading
            text="Performance"
            className={styles.DigitalPracticeRAv2_heading}>
          {this._renderInfoTooltip()}
          <div className={styles.DigitalPracticeRAv2_headingActions}>
            { false ? (
              <button className={styles.DigitalPracticeRAv2_exportBtn} type="button">
                Export
              </button>
            ) : null}
          </div>
        </PageCard.Heading>
        <PageCard.Content className={styles.DigitalPracticeRAv2_content}>
          {(! students || students.size === 0) && ! isLoading ? (
            <div className={styles.DigitalPracticeRAv2_empty}>No Results.</div>
          ) : (
            <div className={styles.DigitalPracticeRAv2_tableLayout}>
              <div className={styles.DigitalPracticeRAv2_panel}
                  ref={(c) => { this._panelRef = c; }}>
                <table className={styles.DigitalPracticeRAv2_table}>
                  {this._renderPanelHeader()}
                  <tbody>
                    {students && students.map((student) =>
                      this._renderPanelRow(student)
                    )}
                  </tbody>
                </table>
              </div>
              <div className={styles.DigitalPracticeRAv2_tableContainer}
                  ref={(c) => { this._tableRef = c; }}>
                <table className={styles.DigitalPracticeRAv2_table}>
                  {this._renderMainTableHeader(unitInfos)}
                  <tbody>
                    {students && students.map((student) =>
                      this._renderMainTableRow(student, unitInfos)
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </PageCard.Content>
      </PageCard>
    );
  }
}

DigitalPracticeRAv2.propTypes = {
  students: PropTypes.object,
  isLoading: PropTypes.bool,
  hideExportButton: PropTypes.bool,
  groupId: PropTypes.string,
  groupLevel: PropTypes.number,
  defaultUnitIds: PropTypes.array,
  fetchRAv2HomeworkResultDetailIfNeeded: PropTypes.func.isRequired,
};

DigitalPracticeRAv2.defaultProps = {
  hideExportButton: true,
};

function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const students = RAv2HomeworkResultDetailAccessors
    .getRAv2HomeworkResultDetailByGroup(state, groupId);
  const isLoading = RAv2HomeworkResultDetailAccessors
    .isLoadingRAv2HomeworkResultDetail(state, groupId);
  const defaultSession = Session.getDefaultSession(state, groupId);
  const defaultUnitIds = defaultSession && defaultSession.unitIds
    ? defaultSession.unitIds : [];
  const group = state.get('rootReducer').get('groups').get(groupId);
  const courseTypeLevelId = group && group.data && group.data.courseTypeLevelId;
  const groupLevel = getRAv2LevelNumber(courseTypeLevelId);
  return { students, isLoading, groupId, groupLevel, defaultUnitIds };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchRAv2HomeworkResultDetailIfNeeded:
      RAv2HomeworkResultDetailActions.fetchRAv2HomeworkResultDetailIfNeeded,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(DigitalPracticeRAv2);



// WEBPACK FOOTER //
// ./containers/Group/DigitalPracticeRAv2/component.jsx